import { useQueryClient } from '@tanstack/react-query';
import {
  type ComponentRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { useAuthInfo } from '@/allauth_auth/hooks';
import {
  getActiveImportKinds,
  useImportStatus,
} from '@/hooks/integrations/useImportStatus';
import { useRecentImportItems } from '@/hooks/integrations/useRecentImportItems';
import { useAiActionsViewPref } from '@/hooks/useAiActionsSortPref';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { useOnboardingDismissal } from '@/hooks/useOnboardingDismissal';
import { LoadingIndicator } from '@/lib/base/foundations/loading-indicator';
import { EdgeResizeHandle } from '@/lib/base/resize-handle/edge-resize-handle';
import {
  type AiActionsView,
  DEFAULT_AI_ACTIONS_VIEW,
} from '@/lib/localStorage/aiActionsSortPrefs';
import { useViewHealthMarker } from '@/lib/view-health';
import { OnboardingImportState } from '@/pages/dashboard/components/OnboardingImportState';

import type {
  ProposalDetail,
  ProposalListItem,
} from '../../../lib/proposals/core/types';
import { useProposalOptional } from '../../../lib/proposals/hooks/useProposal';
import { useMyPendingProposals } from '../../../lib/proposals/hooks/useProposals';
import { getTriggerActionIds } from '../../../lib/proposals/hooks/useTriggerPreview';
import { proposalQueryKeys } from '../../../lib/proposals/query/keys';
import {
  proposalDetailQuery,
  triggerQuery,
} from '../../../lib/proposals/query/queries';
import { isWelcomeProposal } from '../../../lib/proposals/utils/onboardingConfig';
import { AgentFeedContent } from '../AgentFeedContent';
import ProposalDetailPanel from '../ProposalDetailPanel';
import { ProposalNoAccessState } from '../ProposalNoAccessState';
import { getFirstProposalToSelect } from '../shared/utils';
import ProposalListSidebar from '../sidebar-list/ProposalListSidebar';

// /actions/{segment} URLs that name a view tab (as opposed to an action id).
const VIEW_URL_SEGMENTS: readonly AiActionsView[] = [
  'open',
  'done',
  'dismissed',
  'archived',
];

/** The view tab an action belongs to, from its resolution. */
function viewForResolution(resolution: string): AiActionsView {
  switch (resolution) {
    case 'approved':
      return 'done';
    case 'rejected':
      return 'dismissed';
    case 'withdrawn':
      return 'archived';
    default:
      // pending/executing — and any out-of-set legacy value — live in Open.
      return 'open';
  }
}

/** Desktop list-panel width bounds (px) shared by drag and keyboard resize. */
const LIST_PANEL_INITIAL_WIDTH = 380;
const LIST_PANEL_MIN_WIDTH = 280;
const LIST_PANEL_MAX_WIDTH = 600;

function clampListPanelWidth(width: number): number {
  return Math.min(Math.max(width, LIST_PANEL_MIN_WIDTH), LIST_PANEL_MAX_WIDTH);
}

export default function ProposalCenter() {
  const { orgId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  // Extract actionId from path since we use a wildcard route (actions/*)
  // to keep the component mounted across sub-route navigations
  const actionId = useMemo(() => {
    const match = location.pathname.match(/\/actions\/(\d+)/);
    return match ? match[1] : undefined;
  }, [location.pathname]);
  // Lets handleFirstHistoryItem read the current actionId without depending
  // on it, so its identity stays stable across action-id navigation.
  const actionIdRef = useRef(actionId);
  actionIdRef.current = actionId;

  // A /actions/{view} URL selects a tab directly (e.g. /actions/dismissed).
  const urlView = useMemo(() => {
    const match = location.pathname.match(/\/actions\/([a-z-]+)/);
    const segment = match?.[1];
    return VIEW_URL_SEGMENTS.find(v => v === segment);
  }, [location.pathname]);

  const agentFeedEnabled = useFeatureFlag('agent_feed');
  const isAgentFeedRoute = useMemo(
    () => agentFeedEnabled && location.pathname.includes('/actions/agent-feed'),
    [agentFeedEnabled, location.pathname]
  );

  const queryClient = useQueryClient();
  // Category selection removed — groups are always visible with collapse/expand
  const actionListRef = useRef<ComponentRef<typeof ProposalListSidebar>>(null);

  // Auth for import tracking
  const { user } = useAuthInfo();
  const userId = user?.id;

  // Import status
  const importStatus = useImportStatus({ orgId: orgId ?? '' });

  // Live feed of just-imported emails/meetings for the onboarding card ticker.
  // Only polled while an import is actually running, and scoped to the kinds
  // still importing so a finished email import stops feeding the ticker.
  const activeImportKinds = useMemo(
    () => getActiveImportKinds(importStatus.services),
    [importStatus.services]
  );
  const { data: recentImportItems } = useRecentImportItems({
    orgId: orgId ?? '',
    enabled: importStatus.hasActiveImports,
    activeKinds: activeImportKinds,
  });

  // Onboarding dismissal state - uses shared hook (no navigation, no full lifecycle tracking)
  const { showOnboarding, handleDismiss: handleOnboardingDismiss } =
    useOnboardingDismissal({
      orgId: orgId ?? '',
      userId,
      importStatus,
      storageKeyPrefix: 'proposal_center_onboarding_dismissed',
      dismissEventName: 'proposal_center_onboarding_dismissed',
      clearOnFirstCompletion: false,
      trackFullLifecycle: false,
    });

  // Fetch proposal using React Query (only when actionId is present)
  const {
    data: fetchedAction,
    isPlaceholderData,
    isError: isDetailError,
  } = useProposalOptional(
    orgId ? parseInt(orgId) : 0,
    actionId ? parseInt(actionId) : undefined
  ) as {
    data: ProposalDetail | undefined;
    isLoading: boolean;
    isPlaceholderData: boolean;
    isError: boolean;
  };

  // Derive selectedAction that respects absence of actionId
  // This prevents keepPreviousData from showing stale data when navigating away
  // On mobile, skip placeholder (stale) data — it causes a flash of the previous action
  // content with a loader overlay. Mobile navigates to a full-screen detail view so
  // showing stale content is more jarring than a clean loading state.
  const selectedAction =
    actionId && !(isPlaceholderData && window.innerWidth < 1024)
      ? fetchedAction
      : undefined;

  // Which view tab should be active. Priority: an explicit /actions/{view}
  // URL segment, then the viewed action's resolution (navigating directly to
  // an action selects the tab it belongs to), then the view hint carried in
  // navigation state (in-app navigations to /actions/{id} pass the tab they
  // originated from), then the persisted preference (bare /actions).
  //
  // The resolution-derived view is a SNAPSHOT taken when the action loads —
  // not a live derivation — so resolving a proposal in place (dismiss,
  // approve/execute) does NOT drag the tab to Dismissed/Done while
  // handleActionComplete navigates on to the next action. The one exception
  // is a transition back to `pending` (reopen): that's the only resolution
  // change where the user stays on the proposal, and it returns the tab to
  // Open.
  const [prefView] = useAiActionsViewPref();
  const [derivedView, setDerivedView] = useState<AiActionsView | undefined>(
    undefined
  );
  // Reset the snapshot the moment the viewed action changes — during render,
  // not in an effect (React's "adjust state on prop change" pattern). An
  // effect-based reset is one frame late: for that frame the previous
  // action's stale snapshot would win the viewOverride chain and get adopted
  // into the list panel's tab state, flashing the old tab.
  const [snapshotActionId, setSnapshotActionId] = useState(actionId);
  if (snapshotActionId !== actionId) {
    setSnapshotActionId(actionId);
    setDerivedView(undefined);
  }
  useEffect(() => {
    if (!actionId || !fetchedAction || isPlaceholderData) {
      return;
    }
    const next = viewForResolution(fetchedAction.resolution);
    setDerivedView(prev => {
      if (prev === undefined) {
        return next; // initial snapshot for this action
      }
      if (next === 'open') {
        return next; // reopened — the action returned to pending
      }
      return prev; // resolved in place — keep the tab; navigation follows
    });
  }, [actionId, fetchedAction, isPlaceholderData]);

  // View hint from navigation state: in-app navigations to /actions/{id}
  // pass the tab they originated from so the tab has a synchronous source of
  // truth while the action detail (and its resolution snapshot) is still
  // loading. Deep links carry no state and fall back to the snapshot/pref,
  // as before. Sits BELOW derivedView so the reopen exception above still
  // returns the tab to Open.
  const stateView = useMemo(() => {
    const raw = (location.state as { view?: unknown } | null)?.view;
    return VIEW_URL_SEGMENTS.find(v => v === raw);
  }, [location.state]);

  // Bare /actions (no view segment, no action id, no in-app view hint) always
  // lands on the Open tab: entering AI Actions never restores a resolved tab
  // from the persisted preference. Done/Dismissed/Archived are reached only by
  // explicit navigation — the view switcher (/actions/{view}), an action URL
  // whose resolution picks its tab, or an in-app view hint (which the
  // back-to-list navigations below carry so closing a resolved action's
  // detail stays on its tab). The persisted pref remains only as the
  // transient fallback while a deep-linked action's resolution snapshot
  // loads.
  const viewOverride =
    urlView ??
    derivedView ??
    stateView ??
    (actionId == null ? DEFAULT_AI_ACTIONS_VIEW : undefined);
  const effectiveView = viewOverride ?? prefView;
  // Lets navigation callbacks read the active view without depending on it
  // (same stable-identity pattern as actionIdRef above).
  const effectiveViewRef = useRef(effectiveView);
  effectiveViewRef.current = effectiveView;

  // Set data-proposal-open on body when a proposal route is active.
  // The CSS media query in design-system.css handles the mobile-only behavior
  // (hiding nav header, removing padding). Setting the attribute unconditionally
  // avoids race conditions from window.innerWidth checks and ensures the nav
  // hides immediately on navigation (no flash).
  useEffect(() => {
    if (actionId || isAgentFeedRoute) {
      document.body.setAttribute('data-proposal-open', 'true');
    } else {
      document.body.removeAttribute('data-proposal-open');
    }
    return () => {
      document.body.removeAttribute('data-proposal-open');
    };
  }, [actionId, isAgentFeedRoute]);

  // Fetch proposals list to auto-select first one when none selected
  const { data: proposalsResponse } = useMyPendingProposals(
    orgId ? parseInt(orgId) : 0,
    {
      enabled: !!orgId,
    }
  ) as {
    data:
      | {
          results: ProposalListItem[];
          filter_tag_counts?: Record<string, number>;
        }
      | undefined;
    isLoading: boolean;
  };

  // ── View-health markers ───────────────────────────────────────────────
  // The `actions/*` route has three success variants (see dashboard.routes.tsx):
  //   LOADED           — list resolved with items AND list panel rendered
  //   EMPTY            — list resolved with zero items
  //   PROPOSAL_LOADED  — LOADED + detail resolved AND detail panel rendered
  //
  // `actions-list-ready` and `actions-list-empty-ready` are mutually exclusive:
  // when the list query resolves, exactly one fires based on result count.
  // `proposal-detail-ready` only fires when an actionId is in the URL and its
  // detail has loaded. The remaining two markers
  // (`actions-list-items-rendered`, `proposal-actions-rendered`) fire from
  // the list/detail panels themselves so they reflect actual render commits
  // rather than just "data arrived at the parent".
  const listSettled = proposalsResponse !== undefined;
  const listHasItems = (proposalsResponse?.results?.length ?? 0) > 0;
  useViewHealthMarker('actions-list-ready', {
    ready: listSettled && listHasItems,
  });
  useViewHealthMarker('actions-list-empty-ready', {
    ready: listSettled && !listHasItems,
  });
  useViewHealthMarker('proposal-detail-ready', {
    ready: Boolean(selectedAction),
  });

  // Check if onboarding actions exist (from filter_tag_counts). The action-less
  // welcome proposal carries no 'onboarding' tag, so it's absent from
  // filter_tag_counts — detect it by identity so onboarding mode still engages
  // when it is the only onboarding item.
  const onboardingCount = proposalsResponse?.filter_tag_counts?.onboarding ?? 0;
  const hasWelcomeProposal = useMemo(
    () => (proposalsResponse?.results ?? []).some(isWelcomeProposal),
    [proposalsResponse?.results]
  );
  const isOnboardingMode = onboardingCount > 0 || hasWelcomeProposal;

  // NOTE: `onboardingStats`, `hasNonOnboardingActions`, `firstActionId` and
  // `hasMoreProposals` used to be computed here and passed to
  // ProposalDetailPanel. The panel never read them (they were destructured as
  // unused `_`-prefixed params), so they were removed when the panel became a
  // shared component across both the legacy and redesigned shells. Do not
  // reintroduce them here without a consumer that actually reads them.

  // The proposal to auto-select: whatever renders first in the list. One rule,
  // shared with ProposalListSidebar via getFirstProposalToSelect (onboarding
  // first, ranked by getOnboardingSortPriority; then server order).
  const firstProposal = useMemo(
    () => getFirstProposalToSelect(proposalsResponse?.results ?? []),
    [proposalsResponse?.results]
  );

  // Auto-select the first proposal when none is selected (desktop only). While
  // imports are still running and there's nothing to show yet (no onboarding
  // work), hold off so the import progress state stays visible instead.
  const shouldAutoSelect = useMemo(
    () =>
      !isAgentFeedRoute &&
      !actionId &&
      // Only the Open view auto-selects from the pending list; history views
      // auto-select their own first row via onFirstHistoryItem below.
      effectiveView === 'open' &&
      !!orgId &&
      !!firstProposal &&
      !(!isOnboardingMode && showOnboarding),
    [
      isAgentFeedRoute,
      actionId,
      effectiveView,
      orgId,
      firstProposal,
      isOnboardingMode,
      showOnboarding,
    ]
  );
  useEffect(() => {
    if (!shouldAutoSelect || window.innerWidth < 1024) {
      return;
    }
    // shouldAutoSelect guarantees effectiveView === 'open'.
    navigate(`/${orgId}/actions/${firstProposal?.id}`, {
      replace: true,
      state: { view: 'open' },
    });
  }, [shouldAutoSelect, orgId, firstProposal, navigate]);

  // Prefetch trigger data for adjacent proposals (N-1 and N+1) so navigation feels instant.
  // Reads cached detail to extract actionId; if detail not yet cached, prefetch is
  // silently skipped — this is intentional best-effort behavior (no worse than today).
  // Also skip when the cached detail is invalidated (isInvalidated): a WS-driven
  // agent revision invalidates a proposal detail via applyProposalEvent's
  // `refetchType: 'active'`, which only forces an immediate refetch for the
  // currently-open proposal — an inactive adjacent proposal's cached detail stays
  // around with its pre-revision actions[0] until something re-reads it, even
  // though isInvalidated is now set. Deriving fetchActionId from that stale
  // snapshot would 404 against the action-detail endpoint, and because
  // fetchActionId/cacheActionId are unified with the live (non-prefetch) path, the
  // resulting null would overwrite the SAME cache slot the live trigger UI reads
  // from. This check is cache-poisoning prevention, not defensive
  // over-engineering — do not remove it.
  // React Query deduplicates in-flight requests, so rapid keyboard navigation is safe.
  useEffect(() => {
    if (!selectedAction?.id || !orgId) {
      return;
    }
    const numericOrgId = parseInt(orgId);
    const adjacent = actionListRef.current?.getAdjacentActionIds(
      selectedAction.id
    );
    if (!adjacent) {
      return;
    }
    const adjacentIds = [adjacent.prev, adjacent.next].filter(
      (id): id is number => id !== null
    );
    for (const adjacentId of adjacentIds) {
      const detailQuery = proposalDetailQuery(numericOrgId, adjacentId);
      const cachedDetail = queryClient.getQueryData<ProposalDetail>(
        detailQuery.queryKey
      );
      const isDetailInvalidated = queryClient.getQueryState(
        detailQuery.queryKey
      )?.isInvalidated;
      if (cachedDetail && !isDetailInvalidated) {
        const { fetchActionId, cacheActionId } =
          getTriggerActionIds(cachedDetail);
        if (fetchActionId) {
          queryClient.prefetchQuery(
            triggerQuery(numericOrgId, adjacentId, fetchActionId, cacheActionId)
          );
        }
      }
    }
  }, [selectedAction?.id, orgId, queryClient]);

  const getNextActionId = useCallback(
    (currentId: number) =>
      actionListRef.current?.getNextAction(currentId) ?? null,
    []
  );

  // A manual tab change navigates to that tab's URL (/actions/{view}). This
  // drops any selected action id (the detail shown doesn't belong to the new
  // segment); the new tab then auto-selects its first row.
  const handleListViewChange = useCallback(
    (view: AiActionsView) => {
      if (orgId) {
        navigate(`/${orgId}/actions/${view}`);
      }
    },
    [orgId, navigate]
  );

  // Auto-select a history view's first row when nothing is selected (the
  // history-view counterpart of the Open list's auto-select above).
  const handleFirstHistoryItem = useCallback(
    (id: number) => {
      if (orgId && !actionIdRef.current && window.innerWidth >= 1024) {
        navigate(`/${orgId}/actions/${id}`, {
          replace: true,
          state: { view: effectiveViewRef.current },
        });
      }
    },
    [orgId, navigate]
  );

  // List panel resize state
  const [listPanelWidth, setListPanelWidth] = useState(
    LIST_PANEL_INITIAL_WIDTH
  ); // pixels
  const [isResizing, setIsResizing] = useState(false);
  const [dragStartX, setDragStartX] = useState(0);
  const [dragStartWidth, setDragStartWidth] = useState(
    LIST_PANEL_INITIAL_WIDTH
  );

  // Apply resize cursor globally while dragging via body style
  useEffect(() => {
    if (isResizing) {
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    } else {
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    }
    return () => {
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizing]);

  const handleActionSelect = useCallback(
    (actionId: number) => {
      if (orgId) {
        navigate(`/${orgId}/actions/${actionId}`, {
          state: { view: effectiveViewRef.current },
        });
      }
    },
    [orgId, navigate]
  );

  const handleActionUpdate = useCallback(
    (updatedAction: ProposalDetail) => {
      // Update React Query cache
      if (orgId && actionId) {
        queryClient.setQueryData(
          proposalQueryKeys.detail(parseInt(orgId), parseInt(actionId)),
          updatedAction
        );
      }
    },
    [orgId, actionId, queryClient]
  );

  const handleActionComplete = useCallback(
    async (precomputedNextActionId?: number) => {
      const completedActionId = selectedAction?.id;

      // Use the precomputed next action ID if available (captured at execution time),
      // otherwise fall back to computing it now (may be stale if list was already updated)
      const resolveNextActionId = (): number | null => {
        if (precomputedNextActionId) {
          return precomputedNextActionId;
        }
        if (completedActionId && actionListRef.current) {
          return actionListRef.current.getNextAction(completedActionId);
        }
        return null;
      };
      const nextActionId = resolveNextActionId();

      // Remove action from list immediately for snappy UX
      if (selectedAction && actionListRef.current) {
        actionListRef.current.removeAction(selectedAction.id);
      }

      // Clear the completed action's detail from cache BEFORE navigating
      // Using setQueryData(undefined) instead of removeQueries because
      // keepPreviousData still shows stale data after removeQueries
      if (completedActionId && orgId) {
        queryClient.setQueryData(
          proposalQueryKeys.detail(parseInt(orgId), completedActionId),
          undefined
        );
      }

      // Navigate to next action or list view FIRST for snappy UX
      // Then invalidate cache in background (list will update shortly after)
      const navigateAfterCompletion = () => {
        if (!orgId) {
          return;
        }
        const isDesktop = window.innerWidth >= 1024;
        if (isDesktop && nextActionId) {
          // Navigate directly to the next sequential action
          navigate(`/${orgId}/actions/${nextActionId}`, {
            state: { view: effectiveViewRef.current },
          });
        } else {
          // Mobile: always go to list view (no auto-select)
          // Desktop with no next action: go to list view (shows empty state if needed)
          // Carry the current view so bare /actions doesn't force the Open
          // tab on a user who was working a resolved tab.
          navigate(`/${orgId}/actions`, {
            state: { view: effectiveViewRef.current },
          });
        }
      };
      navigateAfterCompletion();

      // Mark list caches stale WITHOUT refetching. Approval submits in the
      // background (submitApprovalInBackground), so an eager refetch here
      // races the server-side commit: the response still contains the
      // just-approved proposal, resurrecting the optimistically-removed row
      // for a beat until the confirmation path's invalidate removes it again
      // (the disappear→reappear→disappear flash). The confirmation paths
      // (submitApprovalInBackground / the reject mutation / WS proposal
      // events) all refresh the lists once the resolution has committed.
      if (orgId) {
        queryClient.invalidateQueries({
          queryKey: proposalQueryKeys.lists(parseInt(orgId)),
          refetchType: 'none',
        });
      }
    },
    [selectedAction, orgId, navigate, queryClient]
  );

  const handleCloseDetail = useCallback(() => {
    if (orgId) {
      // If user came from dashboard, go back there instead of actions list
      const fromDashboard = location.state?.from === 'dashboard';
      if (fromDashboard) {
        navigate(`/${orgId}`);
      } else {
        // Carry the current view so closing a resolved action's detail
        // returns to its tab (bare /actions defaults to Open otherwise).
        navigate(`/${orgId}/actions`, {
          state: { view: effectiveViewRef.current },
        });
      }
    }
  }, [orgId, location, navigate]);

  // List panel resize functionality
  const handleResizeStart = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      setDragStartX(e.clientX);
      setDragStartWidth(listPanelWidth);
      setIsResizing(true);
    },
    [listPanelWidth]
  );

  // Keyboard path for EdgeResizeHandle: signed px delta, positive grows.
  const handleKeyboardResize = useCallback((delta: number) => {
    setListPanelWidth(prev => clampListPanelWidth(prev + delta));
  }, []);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizing) {
        return;
      }

      const mouseX = e.clientX;

      // Calculate delta from where drag started
      const deltaX = mouseX - dragStartX;

      // Apply delta to starting width
      const newListPanelWidth = dragStartWidth + deltaX;

      const constrainedWidth = clampListPanelWidth(newListPanelWidth);

      // Use requestAnimationFrame for smooth updates
      requestAnimationFrame(() => {
        setListPanelWidth(constrainedWidth);
      });
    };

    const handleMouseUp = () => {
      if (isResizing) {
        setIsResizing(false);
      }
    };

    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing, dragStartX, dragStartWidth]);

  // Single unified layout: List Panel | (Optional) Detail Panel
  // On mobile when action selected: hide list, show only detail (full-width)
  // On desktop: show both columns with resizable list panel
  const isShowingDetail = Boolean(selectedAction) || isAgentFeedRoute;
  const isShowingOnboarding = !isShowingDetail && showOnboarding;
  const isShowingNoAccess =
    Boolean(actionId) && isDetailError && !selectedAction && !isPlaceholderData;

  return (
    <div
      className={`flex h-full min-h-0 overflow-hidden bg-bg-primary ${isResizing ? 'select-none' : ''}`}
      style={isResizing ? { pointerEvents: 'auto' } : undefined}
    >
      {/* Left Panel - Action List (hidden on mobile when action selected) */}
      {/* Border shows during loading and whenever on desktop (border separates list from detail) */}
      <div
        className={`flex flex-col flex-1 lg:flex-none min-w-0 relative z-20 lg:border-r lg:border-primary ${selectedAction || isAgentFeedRoute || isShowingNoAccess ? 'max-lg:hidden' : ''}`}
        style={{
          // Only apply fixed width on desktop — mobile uses flex-1 for full width
          ...(typeof window !== 'undefined' && window.innerWidth >= 1024
            ? { width: `${listPanelWidth}px`, flexShrink: 0 }
            : {}),
          transition: isResizing ? 'none' : undefined,
        }}
      >
        <div className='flex-1 overflow-hidden'>
          <ProposalListSidebar
            ref={actionListRef}
            selectedActionId={selectedAction?.id}
            onActionSelect={handleActionSelect}
            isAgentFeedSelected={isAgentFeedRoute}
            onViewChange={handleListViewChange}
            viewOverride={viewOverride}
            onFirstHistoryItem={handleFirstHistoryItem}
          />
        </div>

        {/* Resize handle on the right edge of the list panel — the same
            invisible edge strip the slideouts use. Desktop only: on mobile
            the list is full-width and the detail replaces it. */}
        {(selectedAction || isAgentFeedRoute || isShowingNoAccess) && (
          <div className='max-lg:hidden'>
            <EdgeResizeHandle
              edge='right'
              onResizeStart={handleResizeStart}
              onKeyboardResize={handleKeyboardResize}
              width={listPanelWidth}
              minWidth={LIST_PANEL_MIN_WIDTH}
              maxWidth={LIST_PANEL_MAX_WIDTH}
              isResizing={isResizing}
              aria-label='Resize action list. Use left and right arrow keys to resize.'
            />
          </div>
        )}
      </div>

      {/* Right Panel - Agent Feed, Action Detail, Onboarding Import State, or Empty State */}
      {isShowingDetail && isAgentFeedRoute && (
        <div className='w-full h-full lg:flex-1 overflow-hidden min-h-0 min-w-0 relative z-10 bg-[var(--color-bg-secondary)]'>
          <div className='h-full overflow-y-auto'>
            <div className='p-3 sm:p-4 lg:p-6 max-w-[48rem] mx-auto w-full'>
              <AgentFeedContent orgId={orgId ? parseInt(orgId) : null} />
            </div>
          </div>
        </div>
      )}
      {isShowingDetail && !isAgentFeedRoute && selectedAction && (
        <div className='w-full h-full lg:flex-1 overflow-hidden min-h-0 min-w-0 relative z-10'>
          <ProposalDetailPanel
            key={selectedAction.id}
            action={selectedAction as ProposalDetail}
            onClose={handleCloseDetail}
            onUpdate={handleActionUpdate}
            onComplete={handleActionComplete}
            getNextActionId={getNextActionId}
          />
          {/* Show loading overlay only when showing stale data from a DIFFERENT proposal
              (isPlaceholderData from keepPreviousData). When we have seeded data from
              the list, the header/title shows instantly and actions load in background.
            */}
          {isPlaceholderData && (
            <div className='absolute inset-0 z-20 flex items-center justify-center bg-bg-primary/60 backdrop-blur-[1px]'>
              <LoadingIndicator type='line-spinner' size='md' />
            </div>
          )}
        </div>
      )}
      {isShowingOnboarding && (
        /* Onboarding import state - shown when imports are active and no action selected */
        <div className='hidden lg:flex lg:flex-1 items-center justify-center bg-bg-secondary'>
          <OnboardingImportState
            progress={importStatus.combinedProgress}
            estimatedTimeRemaining={importStatus.estimatedTimeRemaining}
            services={importStatus.services}
            recentItems={recentImportItems}
            error={importStatus.error}
            onDismiss={handleOnboardingDismiss}
            showDismissButton={false}
            className='max-w-2xl'
          />
        </div>
      )}
      {isShowingNoAccess && (
        <ProposalNoAccessState
          onBack={handleCloseDetail}
          className='lg:flex-1 overflow-hidden min-h-0 min-w-0 relative z-10'
        />
      )}
      {!isShowingDetail && !isShowingOnboarding && !isShowingNoAccess && (
        /* Empty state when no action is selected */
        <div className='hidden lg:flex lg:flex-1 bg-bg-secondary' />
      )}
    </div>
  );
}
